import React, { useState, useEffect } from 'react';
import WorkItemForm from './WorkItemForm';
import EditRequestModal from './EditRequestModal';
import './WorkItemModal.css';

const WorkItemModal = ({ 
  isOpen, 
  onClose, 
  workItem, 
  showForm, 
  isEditing, 
  onEdit, 
  onDelete, 
  onSubmit,
  onQuickUpdate,
  clientes,
  tiposCliente,
  users,
  canAssign,
  canDelete,
  userData
}) => {
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [pendingHighValueItem, setPendingHighValueItem] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Check if item is edit locked
  const isEditLocked = workItem?.status === 'done' || workItem?.paymentLocked;
  
  // API configuration
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
    : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';
  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  // Check if user can comment
  const canComment = () => {
    if (!userData || !workItem) return false;
    
    // Check if user has Master or Diretoria permissions
    if (userData.permissions?.includes('Master') || userData.permissions?.includes('Diretoria')) {
      return true;
    }
    
    // Check if user is assigned to this work item
    const assignedUserId = typeof workItem.assignedTo === 'string' ? workItem.assignedTo : workItem.assignedTo?._id;
    return assignedUserId === userData._id;
  };

  // Fetch comments
  const fetchComments = async () => {
    if (!workItem?._id) return;
    
    setLoadingComments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${workItem._id}/comments`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  // Load comments when work item changes
  useEffect(() => {
    if (workItem && workItem._id && isOpen) {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workItem, isOpen]);
  
  // Reset tab when work item changes
  useEffect(() => {
    setActiveTab('details');
  }, [workItem]);
  
  // Reset comment states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewComment('');
      setEditingCommentId(null);
      setEditingCommentText('');
      setComments([]);
    }
  }, [isOpen]);

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim() || !workItem?._id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${workItem._id}/comments`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ text: newComment.trim() })
      });
      
      if (response.ok) {
        setNewComment('');
        fetchComments();
      } else {
        const error = await response.json();
        alert('Erro ao adicionar comentário: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao adicionar comentário');
    }
  };

  // Edit comment
  const handleEditComment = async (commentId) => {
    if (!editingCommentText.trim() || !workItem?._id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${workItem._id}/comments/${commentId}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ text: editingCommentText.trim() })
      });
      
      if (response.ok) {
        setEditingCommentId(null);
        setEditingCommentText('');
        fetchComments();
      } else {
        const error = await response.json();
        alert('Erro ao editar comentário: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao editar comentário');
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este comentário?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${workItem._id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        fetchComments();
      } else {
        const error = await response.json();
        alert('Erro ao excluir comentário: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao excluir comentário');
    }
  };
  
  // Early return after all hooks
  if (!isOpen) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'todo': return 'A Fazer';
      case 'inProgress': return 'Em Progresso';
      case 'done': return 'Concluído';
      default: return status;
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!workItem) return { total: 0, completed: 0, percentage: 0 };
    
    const totalItems = (workItem.selectedServicos?.length || 0) + 
                      (workItem.customActivities?.length || 0) + 
                      (workItem.reembolsos?.length || 0);
    
    const completedItems = (workItem.selectedServicos?.filter(s => s.completed).length || 0) +
                          (workItem.customActivities?.filter(a => a.completed).length || 0) +
                          (workItem.reembolsos?.filter(r => r.completed).length || 0);
    
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    
    return { total: totalItems, completed: completedItems, percentage };
  };

  const progress = calculateProgress();

  // Check for high value additions
  const checkHighValueAddition = async (type, item) => {
    if (item.valor > 150 && workItem.status === 'inProgress') {
      setPendingHighValueItem({ type, ...item });
      if (window.confirm(`Este item possui valor acima de R$ 150,00 e requer aprovação do cliente. Deseja enviar para aprovação?`)) {
        await requestHighValueApproval({ type, ...item });
      }
      return false;
    }
    return true;
  };
  
  // Request high value approval
  const requestHighValueApproval = async (item) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/editRequest/work-items/${workItem._id}/high-value-approval`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ item })
      });

      if (response.ok) {
        alert('Solicitação de aprovação enviada ao cliente!');
      } else {
        const error = await response.json();
        alert('Erro ao solicitar aprovação: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao solicitar aprovação: ' + error.message);
    }
  };
  
  // Handle edit request
  const handleEditRequest = async (items) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/editRequest/work-items/${workItem._id}/edit-request`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        alert('Solicitação de edição enviada com sucesso!');
        setShowEditRequestModal(false);
        onClose();
      } else {
        const error = await response.json();
        alert('Erro ao enviar solicitação: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao enviar solicitação: ' + error.message);
    }
  };

  // Handle checkbox change for selectedServicos
  const handleServicoCheckChange = async (index, checked) => {
    if (!workItem || !onQuickUpdate) return;
    
    // Check if item is edit locked
    if (isEditLocked) {
      if (workItem.paymentLocked) {
        alert('Este item está bloqueado pois já recebeu pagamento.');
      } else {
        alert('Este item está bloqueado para edição. Crie uma solicitação de edição para modificá-lo.');
      }
      return;
    }
    
    // Check if item is authorized
    if (!workItem.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar atividades como concluídas.');
      return;
    }
    
    const updatedServicos = [...workItem.selectedServicos];
    updatedServicos[index] = { ...updatedServicos[index], completed: checked };
    
    await onQuickUpdate(workItem._id, {
      ...workItem,
      selectedServicos: updatedServicos
    });
  };

  // Handle checkbox change for customActivities
  const handleActivityCheckChange = async (index, checked) => {
    if (!workItem || !onQuickUpdate) return;
    
    // Check if item is edit locked
    if (isEditLocked) {
      if (workItem.paymentLocked) {
        alert('Este item está bloqueado pois já recebeu pagamento.');
      } else {
        alert('Este item está bloqueado para edição. Crie uma solicitação de edição para modificá-lo.');
      }
      return;
    }
    
    // Check if item is authorized
    if (!workItem.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar atividades como concluídas.');
      return;
    }
    
    const updatedActivities = [...workItem.customActivities];
    updatedActivities[index] = { ...updatedActivities[index], completed: checked };
    
    await onQuickUpdate(workItem._id, {
      ...workItem,
      customActivities: updatedActivities
    });
  };

  // Handle checkbox change for reembolsos
  const handleReembolsoCheckChange = async (index, checked) => {
    if (!workItem || !onQuickUpdate) return;
    
    // Check if item is edit locked
    if (isEditLocked) {
      if (workItem.paymentLocked) {
        alert('Este item está bloqueado pois já recebeu pagamento.');
      } else {
        alert('Este item está bloqueado para edição. Crie uma solicitação de edição para modificá-lo.');
      }
      return;
    }
    
    // Check if item is authorized
    if (!workItem.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar atividades como concluídas.');
      return;
    }
    
    const updatedReembolsos = [...workItem.reembolsos];
    updatedReembolsos[index] = { ...updatedReembolsos[index], completed: checked };
    
    await onQuickUpdate(workItem._id, {
      ...workItem,
      reembolsos: updatedReembolsos
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{showForm ? (isEditing ? 'Editar Item de Trabalho' : 'Novo Item de Trabalho') : 'Detalhes do Item'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {showForm ? (
            <WorkItemForm
              workItem={workItem}
              isEditing={isEditing}
              onSubmit={onSubmit}
              onCancel={onClose}
              clientes={clientes}
              tiposCliente={tiposCliente}
              users={users}
              canAssign={canAssign}
            />
          ) : workItem ? (
            <>
              <div className="modal-tabs">
                <button 
                  className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  Detalhes
                </button>
                <button 
                  className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('comments')}
                >
                  Comentários
                  {comments.length > 0 && (
                    <span className="comment-count">{comments.length}</span>
                  )}
                </button>
              </div>
              
              <div className="work-item-details">
                {activeTab === 'details' ? (
                  <>
              {!workItem.authorized && (
                <div className="authorization-warning">
                  <div className="warning-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="11" width="14" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </div>
                  <div className="warning-content">
                    <h4>Aguardando Autorização do Cliente</h4>
                    <p>Este item precisa ser autorizado pelo cliente antes que você possa marcar atividades como concluídas ou movê-lo para outra coluna.</p>
                  </div>
                </div>
              )}
              
              {isEditLocked && (
                <div className="edit-lock-warning">
                  <div className="warning-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="11" width="14" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </div>
                  <div className="warning-content">
                    <h4>Item Bloqueado para Edição</h4>
                    <p>
                      {workItem.paymentLocked 
                        ? 'Este item está bloqueado pois já recebeu pagamento. Apenas comentários podem ser adicionados.'
                        : 'Este item está concluído e bloqueado. Para adicionar novos itens, crie uma solicitação de edição.'
                      }
                    </p>
                    {!workItem.paymentLocked && (
                      <button
                        className="btn-request-edit"
                        onClick={() => setShowEditRequestModal(true)}
                      >
                        Solicitar Edição
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              <div className="details-section">
                <h3>Informações Gerais</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Título:</label>
                    <span>{workItem.title}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span>{getStatusLabel(workItem.status)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Prioridade:</label>
                    <span>{getPriorityLabel(workItem.priority)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Data de Vencimento:</label>
                    <span>{formatDate(workItem.dueDate)}</span>
                  </div>
                </div>
                
                {workItem.description && (
                  <div className="detail-item full-width">
                    <label>Descrição:</label>
                    <p>{workItem.description}</p>
                  </div>
                )}
              </div>

              <div className="details-section">
                <h3>Cliente e Tipo</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Cliente:</label>
                    <span>{workItem.clienteData?.nome || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tipo de Cliente:</label>
                    <span>{workItem.tipoClienteData?.nome || '-'}</span>
                  </div>
                </div>
              </div>

              {workItem.selectedServicos?.length > 0 && (
                <div className="details-section">
                  <h3>Serviços do Tipo de Cliente</h3>
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Serviço</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workItem.selectedServicos.map((servico, index) => (
                        <tr key={index} className={servico.completed ? 'completed-row' : ''}>
                          <td>
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={servico.completed || false}
                                onChange={(e) => handleServicoCheckChange(index, e.target.checked)}
                                className="completion-checkbox"
                                disabled={!workItem.authorized}
                                title={!workItem.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                              />
                              <span className={servico.completed ? 'completed-text' : ''}>
                                {servico.nome}
                              </span>
                            </label>
                          </td>
                          <td>{formatCurrency(servico.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {workItem.customActivities?.length > 0 && (
                <div className="details-section">
                  <h3>Atividades Adicionais</h3>
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Atividade</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workItem.customActivities.map((activity, index) => (
                        <tr key={activity.id || index} className={activity.completed ? 'completed-row' : ''}>
                          <td>
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={activity.completed || false}
                                onChange={(e) => handleActivityCheckChange(index, e.target.checked)}
                                className="completion-checkbox"
                                disabled={!workItem.authorized}
                                title={!workItem.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                              />
                              <span className={activity.completed ? 'completed-text' : ''}>
                                {activity.nome}
                              </span>
                            </label>
                          </td>
                          <td>{formatCurrency(activity.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {workItem.reembolsos?.length > 0 && (
                <div className="details-section">
                  <h3>Reembolsos</h3>
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Descrição</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workItem.reembolsos.map((reembolso, index) => (
                        <tr key={reembolso.id || index} className={reembolso.completed ? 'completed-row' : ''}>
                          <td>
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={reembolso.completed || false}
                                onChange={(e) => handleReembolsoCheckChange(index, e.target.checked)}
                                className="completion-checkbox"
                                disabled={!workItem.authorized}
                                title={!workItem.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                              />
                              <span className={reembolso.completed ? 'completed-text' : ''}>
                                {reembolso.descricao}
                              </span>
                            </label>
                          </td>
                          <td>{formatCurrency(reembolso.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {progress.total > 0 && (
                <div className="details-section">
                  <h3>Progresso</h3>
                  <div className="progress-details">
                    <p>{progress.completed} de {progress.total} atividades concluídas ({progress.percentage}%)</p>
                    <div className="progress-bar-details">
                      <div 
                        className="progress-fill-details" 
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="details-section">
                <div className="totals-details">
                  <div className="total-row">
                    <h3>Total Previsto</h3>
                    <span className="total-value previsto">{formatCurrency(workItem.totalValue)}</span>
                  </div>
                  <div className="total-row">
                    <h3>Total Consolidado</h3>
                    <span className="total-value consolidado">{formatCurrency(workItem.totalConsolidado || 0)}</span>
                  </div>
                  <div className="total-row">
                    <h3>Total Pago</h3>
                    <span className="total-value pago">{formatCurrency(workItem.totalPayed || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Informações de Criação e Responsável</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Criado por:</label>
                    <span>{workItem.createdByData?.nomeCompleto || workItem.createdByData?.name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Data de Criação:</label>
                    <span>{formatDate(workItem.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Responsável:</label>
                    <span className={!workItem.assignedToData ? 'unassigned' : ''}>
                      {workItem.assignedToData?.nomeCompleto || workItem.assignedToData?.name || 'Não atribuído'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Data de Atualização:</label>
                    <span>{formatDate(workItem.updatedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="details-actions">
                <button className="btn-secondary" onClick={onEdit} disabled={isEditLocked}>
                  Editar
                </button>
                {canDelete && (
                  <button className="btn-danger" onClick={onDelete}>
                    Excluir
                  </button>
                )}
              </div>
                  </>
                ) : (
                  <div className="comments-section">
                    {loadingComments ? (
                      <div className="comments-loading">Carregando comentários...</div>
                    ) : (
                      <>
                        <div className="comments-list">
                          {comments.length === 0 ? (
                            <div className="no-comments">
                              <p>Nenhum comentário ainda.</p>
                              {canComment() && <p>Seja o primeiro a comentar!</p>}
                            </div>
                          ) : (
                            comments.map(comment => (
                              <div key={comment._id} className="comment-item">
                                <div className="comment-header">
                                  <div className="comment-author">
                                    <strong>{comment.createdBy?.nomeCompleto || 'Usuário'}</strong>
                                    <span className="comment-date">
                                      {formatDate(comment.createdAt)}
                                      {comment.createdAt && ` às ${new Date(comment.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                                    </span>
                                    {comment.edited && <span className="comment-edited">(editado)</span>}
                                  </div>
                                  {userData && comment.createdBy?._id === userData._id && (
                                    <div className="comment-actions">
                                      <button 
                                        className="comment-action-btn"
                                        onClick={() => {
                                          setEditingCommentId(comment._id);
                                          setEditingCommentText(comment.text);
                                        }}
                                        title="Editar"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                      </button>
                                      <button 
                                        className="comment-action-btn delete"
                                        onClick={() => handleDeleteComment(comment._id)}
                                        title="Excluir"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polyline points="3 6 5 6 21 6" />
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div className="comment-body">
                                  {editingCommentId === comment._id ? (
                                    <div className="comment-edit-form">
                                      <textarea
                                        value={editingCommentText}
                                        onChange={(e) => setEditingCommentText(e.target.value)}
                                        className="comment-edit-input"
                                        rows="3"
                                      />
                                      <div className="comment-edit-actions">
                                        <button 
                                          className="btn-save-comment"
                                          onClick={() => handleEditComment(comment._id)}
                                        >
                                          Salvar
                                        </button>
                                        <button 
                                          className="btn-cancel-comment"
                                          onClick={() => {
                                            setEditingCommentId(null);
                                            setEditingCommentText('');
                                          }}
                                        >
                                          Cancelar
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p>{comment.text}</p>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        
                        {canComment() && (
                          <div className="comment-form">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Adicione um comentário..."
                              className="comment-input"
                              rows="3"
                            />
                            <button 
                              className="btn-add-comment"
                              onClick={handleAddComment}
                              disabled={!newComment.trim()}
                            >
                              Adicionar Comentário
                            </button>
                          </div>
                        )}
                        
                        {!canComment() && comments.length > 0 && (
                          <div className="comment-permission-notice">
                            <p>Apenas o responsável e usuários com permissões Master ou Diretoria podem comentar.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
      
      {showEditRequestModal && (
        <EditRequestModal
          isOpen={showEditRequestModal}
          onClose={() => setShowEditRequestModal(false)}
          workItem={workItem}
          onSubmit={handleEditRequest}
          tiposCliente={tiposCliente}
        />
      )}
    </div>
  );
};

export default WorkItemModal;
